import { AxiosInstance } from "axios"
import { GenericResponse } from "."

export interface ParcelModel {
    total: number
    parcels: []
}

export interface ParcelListRequest {
    from: string
    to: string
}
export interface paginationData {
    page?: number
    pageSize?: number
    warehouse?: string
    invoice_type?: number
    customer?: number
    parcelSearch?: {
        uuid: string
        customer: string
        country: string
        suite: string
        status: {
            value: string
            label: string
        }
        date: string
        split: string
        deleted: string
    }
}
export interface AddParcelModel {
    awb: string
    user_pin: string
    can_consolidate: number
}
export interface AddressChangeModel {
    id: number
    address: string
}

export interface parcelNumber {
    uuid: string
}

export interface parcelByIdModal {
    id: number
}
export interface parcelSupplierQueryModal {
    supplier?: string
}

const parcelApi = (apiInstance: AxiosInstance) => {
    const parcelsList = async (data: paginationData): Promise<GenericResponse> => {
        let params = data.page ? "page=" + data.page : "page=1"
        params += data.warehouse ? "&warehouse=" + data.warehouse : ""
        params += data.pageSize ? "&page_size=" + data.pageSize : ""
        params += data.parcelSearch?.uuid ? "&parcel_uuid=" + data.parcelSearch.uuid : ""
        params += data.parcelSearch?.customer ? "&user_pin=" + data.parcelSearch.customer : ""
        params += data.parcelSearch?.country ? "&destination=" + data.parcelSearch.country : ""
        params += data.parcelSearch?.suite ? "&suite_uuid=" + data.parcelSearch.suite : ""
        params += data.parcelSearch?.status?.value ? "&status=" + data.parcelSearch.status.value : ""
        params += data.parcelSearch?.date ? "&date_range=" + data.parcelSearch.date : ""
        params +=
            data.parcelSearch?.split && data.parcelSearch?.split !== ""
                ? "&split=" + data.parcelSearch.split
                : ""
        params +=
            data.parcelSearch?.deleted && data.parcelSearch?.deleted !== ""
                ? "&deleted=" + data.parcelSearch.deleted
                : ""
        let url = "/parcels/get-parcels"
        url = params ? url + "?" + params : ""

        return apiInstance.get(url)
    }

    const parcelById = async (data: parcelNumber): Promise<GenericResponse> => {
        return apiInstance.get("/parcel/multi-box/" + data.uuid)
    }

    const addParcel = async (parcelData: AddParcelModel): Promise<any> => {
        return apiInstance.post("/parcels/receive", parcelData)
    }
    const editParcel = async (parcelData: any): Promise<any> => {
        const id = parcelData.id
        delete parcelData.id
        const addParcelDetail = apiInstance.post("/parcels/update-parcel/" + id, parcelData)
        return addParcelDetail
    }

    const addParcelImage = async (parcelData: any): Promise<any> => {
        const id = parcelData.id
        delete parcelData.id
        const addParcelDetail = apiInstance.post("/parcels/image/upload/" + id, parcelData)
        return addParcelDetail
    }
    const addressChange = async (parcelData: any): Promise<any> => {
        const id = parcelData.id
        delete parcelData.id
        const addParcelDetail = apiInstance.put("/parcels/address-update/" + id, parcelData)
        return addParcelDetail
    }
    const deleteParcelImage = async (parcelData: any): Promise<any> => {
        const id = parcelData.id
        delete parcelData.id
        const addParcelDetail = apiInstance.delete("/parcels/image/remove/" + id, {
            data: { file_id: parcelData.file_id }
        })
        return addParcelDetail
    }

    const loadMarkReasons = async (): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get("/parcels/exception-reasons")
        return res
    }

    const markAsException = async (parcelData: any): Promise<any> => {
        const id = parcelData.id
        delete parcelData.id
        const addParcelDetail = apiInstance.post("/parcels/mark-exception/" + id, parcelData)
        return addParcelDetail
    }

    const loadParcelDestinations = async (): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get("/parcels/unique-destinations")
        return res
    }

    const loadParcelStatuses = async (): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get("/parcels/statuses")
        return res
    }

    const loadParcelPercentageById = async (data: parcelByIdModal): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get("/parcels/completion-percentage/" + data.id)
        return res
    }

    const supplierAutocomplete = async (data: parcelSupplierQueryModal): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get("/parcels/supplier?")
        return res
    }

    const itemsAutocomplete = async (): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get("/parcels/info-autocomplete")
        return res
    }

    const deleteParcel = async (parcelId: number): Promise<any> => {
        const addParcelDetail = apiInstance.post("/parcels/delete-parcel/" + parcelId, {})
        return addParcelDetail
    }

    const creditParcelsList = async (data: paginationData): Promise<GenericResponse> => {
        var params = data.page ? "page=" + data.page : "page=1"
        params += data.warehouse ? "&warehouse=" + data.warehouse : ""
        params += data.invoice_type ? "&type=" + data.invoice_type : ""
        params += data.customer ? "&user_id=" + data.customer : ""

        params += data.parcelSearch?.date ? "&date_range=" + data.parcelSearch.date : ""
        var url = "/parcels/credit-parcels"
        url = params ? url + "?" + params : ""
        const res: Promise<GenericResponse> = apiInstance.get(url)
        return res
    }

    const invoiceParcel = async (parcelIds: number[]): Promise<any> => {
        const addParcelDetail = apiInstance.post("/parcels/invoice-credit-parcels", { parcel_ids: parcelIds })
        return addParcelDetail
    }

    const splitParcels = async (data: Record<string, any>): Promise<any> => {
        const { parcelId, newParcels = [] } = data
        const splitParcelsData = apiInstance.post("/parcels/split-parcel/" + parcelId, newParcels)
        return splitParcelsData
    }

    const uploadFile = async (data: Record<string, any>): Promise<any> => {
        const uploadFileResult = apiInstance.post("/parcels/file-upload", data)

        return uploadFileResult
    }

    const regenerateLabels = async (data: Record<string, any>): Promise<any> => {
        const regenerateLabelsResult = apiInstance.post("/orders-parcels/regenerate", data)
        return regenerateLabelsResult
    }

    return {
        parcelsList,
        addParcel,
        parcelById,
        editParcel,
        addressChange,
        addParcelImage,
        deleteParcelImage,
        loadMarkReasons,
        markAsException,
        loadParcelDestinations,
        loadParcelStatuses,
        loadParcelPercentageById,
        supplierAutocomplete,
        deleteParcel,
        itemsAutocomplete,
        creditParcelsList,
        invoiceParcel,
        splitParcels,
        uploadFile,
        regenerateLabels
    }
}

export default parcelApi
